import { utils, LitElement, html, broadcastReceiver } from '@hh/global.js'

const {
  serializeForm,
  getCart,
  addToCart,
  updateCart,
  reRenderShopifySections,
} = utils

class HHProductForm extends LitElement {
  static properties = {
    showMiniCartAfterSubmission: {
      type: Boolean,
      attribute: 'show-mini-cart',
    },
    productType: {
      type: String,
      attribute: 'product-type',
    },
  }

  get isMedal() {
    return this.productType === 'Medal'
  }

  get customizationMethod() {
    return window.customizer?.payload?.customization_method || 'full'
  }

  constructor() {
    super()

    this.properties = {}
    this.form = this.querySelector('form')
    this.quanityInput = this.querySelector('[name="quantity"]')
    this.submitButton = this.querySelector('[type="submit"]')
    this.submitWrapper = this.querySelector('.js-submit-wrapper')
    this.personalizeButton = this.querySelector('.js-personalize-trigger')

    this.form.addEventListener('submit', this.onSubmitHandler.bind(this))
  }

  connectedCallback() {
    super.connectedCallback()

    this.quantityReceiver = broadcastReceiver('quantity', (...args) => {
      this.getQuantityFromDocument(...args)
    })

    this.personalizeReceiver = broadcastReceiver('personalize', (...args) => {
      this.getPersonalizeOptionFromDocument(...args)
    })

    document.addEventListener('closeModal', () => {
      document.body.removeAttribute('modal')
    })

    document.addEventListener('cart:forceAddToCart', (event) => {
      const { properties, quantity } = event?.detail
      this.addToCart(properties, quantity)
      document.body.removeAttribute('modal')
    })

    document
      .querySelector('[href="#highlights"]')
      ?.addEventListener('click', this.expandBulkPrice.bind(this))
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.quantityReceiver?.disconnect()
    this.personalizeReceiver?.disconnect()
  }

  getQuantityFromDocument(data) {
    if (this.quanityInput) {
      this.quanityInput.value = data.value || 1
    }
  }

  getPersonalizeOptionFromDocument(data) {
    this.personalizeButton.classList[data.value === 'Yes' ? 'remove' : 'add'](
      'hidden'
    )
    this.submitWrapper.classList[data.value === 'Yes' ? 'add' : 'remove'](
      'hidden'
    )
  }

  expandBulkPrice(e) {
    e.preventDefault()

    const target = document.querySelector('[data-type="bulk_pricing"]')

    if (!target) return

    target.scrollIntoView({
      behavior: 'smooth',
    })

    target.querySelector('details')?.setAttribute('open', true)
  }

  onSubmitHandler(event) {
    event.preventDefault()

    if (!!window.customizer.payload) {
      document.body.setAttribute('modal', '{"id": "personalize-no-confirm"}')
    } else {
      this.addToCart()
    }
  }

  async addToCart(properties = {}, quantity = null) {
    if (!!this.submitButton) {
      this.submitButton.setAttribute('disabled', true)
      this.submitButton.classList.add('loading')
    }
    const data = JSON.parse(serializeForm(this.form))

    data.properties = Object.assign(
      {
        ...data.properties,
        ...properties,
      },
      data.personalize === 'Yes'
        ? {
            _customization_method: this.customizationMethod,
          }
        : {}
    )

    if (quantity) {
      data['quantity'] = quantity
    }

    await this.updateCartProof(data.properties)

    reRenderShopifySections(await addToCart(data))

    if (this.showMiniCartAfterSubmission) {
      utils.toggle(document.body, ['mini-cart-is-active'])
    }

    this.dispatchEvent(
      new CustomEvent('productAdded', {
        bubbles: true,
        composed: true,
        detail: {
          id: 'cart-notification',
        },
      })
    )

    if (!!this.submitButton) {
      this.submitButton.removeAttribute('disabled')
      this.submitButton.classList.remove('loading')
    }
  }

  async updateCartProof(properties) {
    if (
      this.isMedal ||
      properties?._customization_method !== 'full'
    ) {
      return
    }

    const { items } = await getCart()
    const hasUploadedFile =
      !!properties?._uploaded_file ||
      items.some(
        (item) =>
          item?.properties?._customization_method === 'full' &&
          !!item?.properties?._uploaded_file
      )
    const hasFullDesign =
      !!properties?._canvas ||
      items.some(
        (item) =>
          item?.properties?._customization_method === 'full' &&
          !!item?.properties?._canvas
      )

    const proof = hasFullDesign
      ? 'approved'
      : hasUploadedFile
        ? 'unapproved'
        : 'unapproved'

    await updateCart({
      attributes: {
        _proof: proof,
      },
    })
  }

  render() {
    return html`<slot></slot>`
  }
}

customElements.define('hh-product-form', HHProductForm)
